/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, useTheme, Grid, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { ROUTE_PATH } from "../../constants/route";
import { AddEditProductSchema } from "../../schema/addEditProductSchema";
import TextFeild from "../../components/CommonComponents/TextFeild";
import MUIMultipleSelect from "../../components/CommonComponents/Select/MUIMultipleSelect";
import SelectFeild from "../../components/CommonComponents/Select";
import RichTextEditor from "../../components/CommonComponents/RichTextEditor";
import { INPUT_LABELS } from "../../constants/lang";
import {
  fetchCategories,
  fetchTags,
  fetchOptionTypes,
  fetchCreateProduct,
  fetchEditProducts,
  actions,
} from "../../store/reducers/createProfile";
import VariantSection from "./VariantSection";
import MediaSection from "./MediaSection";
import ProductVariantsModal from "./ProductVariantsModal";
import VariantTypeDeleteModal from "./VariantTypeDeleteModal";
import {
  fetchGetProductById,
  fetchUpdateProductOptions,
  fetchOptionTypeDetail,
  fetchDeleteVariant,
  fetchUpdateVariant
} from "../../store/reducers/createProfile/apithunk";
import {
  ToastNotifyError,
  ToastNotifySuccess,
} from "../../components/CommonComponents/Toast/ToastNotify";
import rollbar from "../../rollbarConfig";
import addEditProductStyles from "./addEditProduct.styles";

const statusOptions = ["Active", "InActive"];

const AddEditProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const styles = addEditProductStyles(theme);

  const productVariants = useSelector((state) => state.createProfile?.productVariants || {});
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [optionTypes, setOptionTypes] = useState([]);
  const [availableVariantTypes, setAvailableVariantTypes] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [completedVariants, setCompletedVariants] = useState([]);
  const [variantOptionsMap, setVariantOptionsMap] = useState({});
  const [variantCombinations, setVariantCombinations] = useState([]);
  const [productMedia, setProductMedia] = useState([]);
  const [openVariantImageModal, setOpenVariantImageModal] = useState(false);
  const [currentVariantIndex, setCurrentVariantIndex] = useState(null);
  const [variantSelectedImages, setVariantSelectedImages] = useState({});
  const [hasVariants, setHasVariants] = useState(false);
  const [openVariantModal, setOpenVariantModal] = useState(false);
  const [validatedFormData, setValidatedFormData] = useState(null);
  const [originalVariantQuantities, setOriginalVariantQuantities] = useState({});
  const [originalVariantData, setOriginalVariantData] = useState({});
  const [modifiedVariants, setModifiedVariants] = useState(new Set());
  const [productDataLoaded, setProductDataLoaded] = useState(false);
  const [editingVariantIds, setEditingVariantIds] = useState(new Set());
  const [editingVariantOriginalData, setEditingVariantOriginalData] = useState({});
  const [openDeleteVariantTypeModal, setOpenDeleteVariantTypeModal] = useState(false);
  const [variantTypeToDelete, setVariantTypeToDelete] = useState(null);
  const [originalProductData, setOriginalProductData] = useState(null);
  const isEditMode = !!id;
  const hasFetchedData = useRef(false);
  const lastFetchedProductId = useRef(null);
  const productVariantsDataRef = useRef(null);

  const handleBack = () => {
    navigate(ROUTE_PATH.INVENTORY_SETUP);
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(AddEditProductSchema),
    defaultValues: {
      productName: "",
      Categories: [],
      status: "",
      tags: [],
      description: "",
      price: "",
      quantity: "",
      productMedia: [],
    },
  });

  const onSubmit = async (data) => {
    // Check if any variant has shopify_variant_id
    const hasShopifyVariant = variantCombinations?.some(variant => variant?.variantData?.shopify_variant_id);
    
    // Clear price and quantity validation errors if shopify variant exists
    if (hasShopifyVariant) {
      clearErrors('price');
      clearErrors('quantity');
    }

    if (isEditMode) {
      setValidatedFormData(data);

      try {
        const allVariantData = watch('variantCombinations') || [];
        const validationErrors = [];

        for (let index = 0; index < allVariantData?.length; index++) {
          const variantData = allVariantData[index];
          const quantity = Number(variantData?.quantity) || 0;
          const media = variantData?.media;
          const variantImages = media && Array.isArray(media) ? media : (media ? [media] : []);

          if (quantity > 0 && (!variantImages || variantImages?.length === 0)) {
            validationErrors.push({
              index,
              variantIndex: index,
              message: `Image is a mandatory field.`
            });
            setError(`variantCombinations.${index}.media`, {
              type: 'manual',
              message: 'Image is a mandatory field.'
            });
          }
        }

        if (validationErrors?.length > 0) {
          if (validationErrors?.length === 1) {
            ToastNotifyError(`Image is a mandatory field for variant`);
          } else {
            ToastNotifyError(`Image is a mandatory field for variants.`);
          }
          return;
        }

        const productId = await saveProduct(data);

        if (productId) {
          setModifiedVariants(new Set());
          navigate(ROUTE_PATH.INVENTORY_SETUP);
        } else {
          console.error("Failed to save product");
        }

        return productId;
      } catch (error) {
        console.error("Error in onSubmit:", error);
        rollbar.error("Error saving product in onSubmit", error);
        ToastNotifyError("Failed to save product");
      }
    } else {
      setValidatedFormData(data);
      setOpenVariantModal(true);
    }
  };

  const onError = (errors) => {
    ToastNotifyError("Please fill in all required fields");
  };

  const handleSaveButtonClick = (e) => {
    e.preventDefault();
    handleSubmit(onSubmit, onError)();
  };

  const saveProduct = async (formData = null) => {
    const dataToUse = formData || validatedFormData;
    if (!dataToUse) return;

    try {
      const allVariantData = watch('variantCombinations') || [];
      const hasVariants = variantCombinations?.length > 0 || allVariantData?.length > 0;

      const payload = {};

      if (isEditMode && originalProductData) {
        const currentName = dataToUse?.productName;
        const currentDescription = dataToUse?.description || "";
        const currentPrice = String(dataToUse?.price);
        const currentStatus = dataToUse.status === "Active" ? "A" : "In";
        const currentCategories = dataToUse?.Categories?.map(cat => cat?.id) || [];
        const currentTags = dataToUse?.tags?.map(tag => tag?.id) || [];
        const currentImages = dataToUse?.productMedia || [];

        if (currentName !== (originalProductData?.name || "")) {
          payload.name = currentName;
        }

        if (currentDescription !== (originalProductData?.description || "")) {
          payload.description = currentDescription;
        }

        const currentPriceNum = dataToUse?.price && dataToUse?.price !== ""
          ? Number(dataToUse?.price)
          : null;
        const originalPriceNum = originalProductData?.price && originalProductData?.price !== ""
          ? Number(originalProductData?.price)
          : null;

        const currentIsValid = currentPriceNum !== null && !isNaN(currentPriceNum);
        const originalIsValid = originalPriceNum !== null && !isNaN(originalPriceNum);

        if (!currentIsValid && !originalIsValid) {
        } else if (currentIsValid !== originalIsValid || currentPriceNum !== originalPriceNum) {
          payload.price = currentPrice;
        }

        if (currentStatus !== (originalProductData?.status === "Active" ? "A" : "In")) {
          payload.status = currentStatus;
        }

        const originalCategories = originalProductData?.categories || [];
        const categoriesChanged = JSON.stringify(currentCategories.sort()) !== JSON.stringify(originalCategories.sort());
        if (categoriesChanged) {
          payload.categories = currentCategories;
        }

        const originalTags = originalProductData?.tags || [];
        const tagsChanged = JSON.stringify(currentTags.sort()) !== JSON.stringify(originalTags.sort());
        if (tagsChanged) {
          payload.tags = currentTags;
        }

        const originalImages = originalProductData?.images || [];
        const imagesChanged = JSON.stringify(currentImages.sort()) !== JSON.stringify(originalImages.sort());
        if (imagesChanged) {
          payload.images = currentImages;
        }

        if (!hasVariants) {
          const currentQuantity = dataToUse?.quantity;
          const originalQuantity = originalProductData?.quantity || "";
          if (String(currentQuantity) !== String(originalQuantity)) {
            payload.quantity = currentQuantity;
          }
        }
      } else {
        payload.name = dataToUse?.productName;
        payload.description = dataToUse?.description || "";
        payload.price = String(dataToUse?.price);
        payload.status = dataToUse?.status === "Active" ? "A" : "In";
        payload.categories = dataToUse?.Categories?.map(cat => cat?.id) || [];
        payload.tags = dataToUse?.tags?.map(tag => tag?.id) || [];
        payload.images = dataToUse?.productMedia || [];

        if (!hasVariants) {
          payload.quantity = dataToUse?.quantity;
        }
      }

      if (isEditMode && completedVariants?.length > 0) {
        const optionTypesArray = completedVariants?.map(cv => cv.type?.id)?.filter(Boolean);
        const optionValuesDict = {};
        completedVariants?.forEach(cv => {
          if (cv.type?.id && cv.selectedOptions && Array.isArray(cv.selectedOptions)) {
            const valueIds = cv.selectedOptions?.map(opt => opt.id)?.filter(Boolean);
            if (valueIds?.length > 0) {
              optionValuesDict[String(cv.type?.id)] = valueIds;
            }
          }
        });

        if (originalProductData?.option_types && originalProductData?.option_values_dict) {
          const originalOptionTypes = originalProductData?.option_types || [];
          const originalOptionValuesDict = originalProductData?.option_values_dict || {};

          const optionTypesChanged = JSON.stringify(optionTypesArray?.sort()) !== JSON.stringify(originalOptionTypes?.sort());
          if (optionTypesChanged) {
            payload.option_types = optionTypesArray;
          }

          const optionValuesDictChanged = JSON.stringify(optionValuesDict) !== JSON.stringify(originalOptionValuesDict);
          if (optionValuesDictChanged) {
            payload.option_values_dict = optionValuesDict;
          }
        } else {
          payload.option_types = optionTypesArray;
          payload.option_values_dict = optionValuesDict;
        }

        const variantDataArray = [];
        allVariantData.forEach((variantData) => {
          const variantId = variantData?.id;

          if (variantId && modifiedVariants?.has(variantId)) {
            const quantity = Number(variantData?.quantity) || 0;
            const price = variantData?.price ? String(variantData?.price) : null;
            const media = variantData?.media;
            const variantImages = media && Array.isArray(media) ? media : (media ? [media] : []);

            const variantPayload = {
              id: variantId,
              quantity: quantity,
            };

            if (price) {
              variantPayload.price = price;
            }

            if (variantImages && variantImages?.length > 0) {
              variantPayload.variant_img = variantImages;
            }

            variantDataArray.push(variantPayload);
          }
        });

        if (variantDataArray?.length > 0) {
          payload.variant_data = variantDataArray;
        }
      }

      let productId = id;

      if (isEditMode) {
        const response = await dispatch(fetchEditProducts({
          id: id,
          dataToSend: payload
        }));

        const { status, data, message } = response?.payload || {};
        if (status) {
          productId = data?.id || id;
          ToastNotifySuccess("Product updated successfully!");
          return productId;
        } else {
          ToastNotifyError(message || "Failed to update product");
          return null;
        }
      } else {
        const response = await dispatch(fetchCreateProduct(payload));

        const { status, data, message } = response?.payload || {};
        if (status) {
          productId = data?.id || data?.data?.id;
          ToastNotifySuccess("Product added successfully!");
          return productId;
        } else {
          ToastNotifyError(message || "Failed to create product");
          return null;
        }
      }
    } catch (error) {
      rollbar.error("Error saving product", error);
      ToastNotifyError("Failed to save product");
      return null;
    }
  };

  const handleSaveAsSingleProduct = async () => {
    const productId = await saveProduct();

    if (productId) {
      setOpenVariantModal(false);
      navigate(`/inventory-setup/product-details/${productId}`);
    } else {
      setOpenVariantModal(false);
      navigate(`/inventory-setup/product-details/516`);
    }
  };

  const handleAddVariants = async () => {
    const productId = await saveProduct();

    if (productId) {
      setOpenVariantModal(false);
      navigate(`/inventory-setup/edit-product/${productId}`);
    } else {
      setOpenVariantModal(false);
    }
  };

  useEffect(() => {
    if (hasFetchedData.current) return;

    const fetchAllData = async () => {
      hasFetchedData.current = true;

      try {
        const [tagsRes, categoriesRes, optionTypesRes] = await Promise.all([
          dispatch(fetchTags()),
          dispatch(fetchCategories()),
          dispatch(fetchOptionTypes()),
        ]);

        const { data: tagsData, status: tagsStatus, message: tagsMessage } = tagsRes?.payload || {};
        if (tagsStatus) {
          setTags(tagsData);
        } else {
          ToastNotifyError(tagsMessage);
        }

        const { data: categoriesData, status: categoriesStatus, message: categoriesMessage } = categoriesRes?.payload || {};
        if (categoriesStatus) {
          setCategories(categoriesData);
        } else {
          ToastNotifyError(categoriesMessage);
        }

        const { data: optionTypesData, status: optionTypesStatus, message: optionTypesMessage } = optionTypesRes?.payload || {};
        if (optionTypesStatus) {
          setOptionTypes(optionTypesData);
          setAvailableVariantTypes(optionTypesData);
        } else {
          ToastNotifyError(optionTypesMessage);
        }
      } catch (error) {
        console.error("Data fetch failed:", error);
        rollbar.error("Error message", error);
        hasFetchedData.current = false;
      }
    };

    fetchAllData();
  }, [dispatch]);

  useEffect(() => {
    if (!isEditMode || !id || lastFetchedProductId.current === id) return;

    if (categories?.length === 0 || tags?.length === 0) return;

    const getProduct = async () => {
      lastFetchedProductId.current = id;

      try {
        const res = await dispatch(fetchGetProductById(id));
        if (res?.payload?.status === 404) {
          ToastNotifyError(res?.payload?.response?.data?.detail || "Product not found");
          navigate(ROUTE_PATH.INVENTORY_SETUP);
          return;
        }
        const { data, status, message } = res?.payload || {};

        if (status && data) {
          const statusValue = data.status === "A" ? "Active" : data.status === "In" ? "InActive" : "";

          const selectedCategories = data.categories && Array.isArray(data.categories)
            ? categories?.filter(cat => data.categories.includes(cat.id))
            : [];

          const selectedTags = data.tags && Array.isArray(data.tags)
            ? tags?.filter(tag => data.tags.includes(tag.id))
            : [];

          const originalData = {
            name: data?.name || "",
            description: data?.description || "",
            price: data?.price || "",
            quantity: data?.quantity || "",
            status: statusValue,
            categories: data?.categories || [],
            tags: data?.tags || [],
            images: data?.images || [],
          };
          setOriginalProductData(originalData);

          reset({
            productName: data?.name || "",
            Categories: selectedCategories,
            status: statusValue,
            tags: selectedTags,
            description: data?.description || "",
            price: data?.price || "",
            quantity: data?.quantity || "",
            productMedia: data?.images || [],
          });
          if (data?.images && Array.isArray(data?.images)) {
            setProductMedia(data?.images);
            setValue('productMedia', data?.images);
          }

          if (data?.option_types && data?.option_values_dict) {
            productVariantsDataRef.current = {
              variants: data?.variants || [],
              option_types: data?.option_types || [],
              option_values_dict: data?.option_values_dict || {}
            };
            setHasVariants(true);
            setProductDataLoaded(true);

            setOriginalProductData(prev => ({
              ...(prev || {}),
              option_types: data.option_types || [],
              option_values_dict: data.option_values_dict || {}
            }));
          } else {
            setProductDataLoaded(false);
          }

          if (data?.variants && Array.isArray(data?.variants) && data?.variants?.length > 0) {
            setHasVariants(true);

            const mappedVariants = data?.variants?.map((variant) => {
              const optionValues = [];
              if (variant.options && typeof variant.options === 'object') {
                if (Array.isArray(variant.options)) {
                  optionValues.push(...variant.options);
                } else {
                  const firstKey = Object.keys(variant.options)[0];
                  if (!isNaN(firstKey)) {
                    Object.entries(variant.options)?.forEach(([typeId, valueIds]) => {
                      if (Array.isArray(valueIds)) {
                        valueIds.forEach(valueId => {
                          optionValues.push({ id: valueId, typeId: parseInt(typeId) });
                        });
                      }
                    });
                  }
                }
              }

              return {
                id: variant.id,
                options: variant.options,
                optionValues: optionValues,
                option_values_with_hex: variant.option_values_with_hex,
                price: variant.price || '',
                quantity: variant.quantity || 0,
                media: variant.variant_img || [],
                variant_title: variant.variant_title,
                displayName: variant.display_name || variant.variant_title || `Variant ${variant.id}`,
                display_name: variant.display_name,
                variantData: variant
              };
            });

            setVariantCombinations(mappedVariants);

            const originalQuantities = {};
            const originalData = {};
            mappedVariants.forEach((variant, index) => {
              setValue(`variantCombinations.${index}.id`, variant.id);
              setValue(`variantCombinations.${index}.price`, variant.price);
              setValue(`variantCombinations.${index}.quantity`, variant.quantity);
              setValue(`variantCombinations.${index}.media`, variant.media);

              if (variant.id) {
                originalQuantities[variant.id] = variant.quantity || 0;
                originalData[variant.id] = {
                  price: variant.price || '',
                  quantity: variant.quantity || 0,
                  media: variant.media || []
                };
              }
            });
            setOriginalVariantQuantities(originalQuantities);
            setOriginalVariantData(originalData);
          } else {
            setHasVariants(data?.option_types && data?.option_types?.length > 0);
            setVariantCombinations([]);
          }
        } else {
          ToastNotifyError(message || "Failed to load product details");
        }
      } catch (error) {
        console.error("Product fetch failed:", error);
        rollbar.error("Error fetching product", error);
        ToastNotifyError("Failed to load product details");
        lastFetchedProductId.current = null;
      }
    };

    getProduct();
  }, [id, isEditMode, dispatch, categories, tags, reset, navigate]);

  useEffect(() => {
    if (isEditMode && productDataLoaded && productVariantsDataRef.current && optionTypes?.length > 0) {
      const storedData = productVariantsDataRef.current;

      const productOptionTypes = storedData?.option_types || [];
      const productOptionValuesDict = storedData?.option_values_dict || {};

      if (!Array.isArray(productOptionTypes) || productOptionTypes?.length === 0) {
        return;
      }

      if (Object.keys(productOptionValuesDict)?.length === 0) {
        return;
      }

      const buildCompletedVariantsFromData = async () => {
        const completedVariantsList = [];

        for (const optionTypeId of productOptionTypes) {
          try {
            const optionType = optionTypes?.find(ot => ot.id === optionTypeId);
            if (!optionType) {
              console.warn(`Option type ${optionTypeId} not found in optionTypes array. Available IDs:`, optionTypes?.map(ot => ot.id));
              continue;
            }

            const valueIdsArray = productOptionValuesDict[String(optionTypeId)] || productOptionValuesDict[optionTypeId] || [];

            if (!Array.isArray(valueIdsArray) || valueIdsArray?.length === 0) {
              console.warn(`No value IDs found for option type ${optionTypeId} in option_values_dict`);
              continue;
            }

            const uniqueValueIds = new Set(valueIdsArray);

            const optionValuesRes = await dispatch(fetchOptionTypeDetail(optionTypeId));
            const { data: optionValuesData, status: optionValuesStatus } = optionValuesRes?.payload || {};

            if (optionValuesStatus && optionValuesData && Array.isArray(optionValuesData)) {
              const selectedOptionValues = optionValuesData
                ?.filter(opt => uniqueValueIds.has(opt.id))
                ?.map(opt => ({
                  ...opt,
                  name: opt.name || opt.value || opt.display_name || `Option ${opt.id}`
                }));

              if (selectedOptionValues?.length > 0) {
                completedVariantsList.push({
                  id: `completed_${optionTypeId}_${Date.now()}`,
                  type: optionType,
                  selectedOptions: selectedOptionValues
                });
              } else {
                console.warn(`No matching option values found for type ${optionTypeId}. Fetched values:`, optionValuesData?.map(opt => ({ id: opt.id, name: opt.name || opt.value })));
              }
            } else {
              console.warn(`Failed to fetch option values for type ${optionTypeId}. Status:`, optionValuesStatus);
            }
          } catch (error) {
            console.error(`Error fetching option values for type ${optionTypeId}:`, error);
          }
        }
        setCompletedVariants(completedVariantsList);
        const usedTypeIds = completedVariantsList?.map(cv => cv.type?.id);
        setAvailableVariantTypes(prev =>
          prev.filter(type => !usedTypeIds.includes(type.id))
        );
      };

      buildCompletedVariantsFromData();
    }
  }, [optionTypes, isEditMode, dispatch, productDataLoaded]);

  const handleRemoveVariant = (variantId) => {
    const variantToRemove = selectedVariants?.find((v) => v.id === variantId);
    if (!variantToRemove) return;

    if (variantToRemove && variantToRemove.type) {
      if (!availableVariantTypes?.find((t) => t.id === variantToRemove.type?.id)) {
        setAvailableVariantTypes((prev) => [...prev, variantToRemove.type]);
      }

      setVariantOptionsMap((prev) => {
        const newMap = { ...prev };
        delete newMap[variantToRemove.type?.id];
        return newMap;
      });
    }

    setSelectedVariants((prev) => prev?.filter((v) => v.id !== variantId));

    setValue(`variants.${variantId}.variantType`, "");
    setValue(`variants.${variantId}.variantOption`, "");
    setValue(`variants.${variantId}.varientQuantity`, "");
    setValue(`variants.${variantId}.selectImage`, []);
  };

  const handleVariantDone = async (variantId, variantType, selectedOptions) => {
    const variantData = {
      type: variantType,
      selectedOptions: selectedOptions || [],
      variantOptions: variantOptionsMap[variantType?.id] || [],
    };

    const variantBeingDone = selectedVariants?.find((v) => v.id === variantId);
    const originalCompletedVariantId = variantBeingDone?.originalCompletedVariantId;

    const completedVariantId = `completed_${Date.now()}`;
    dispatch(actions.setProductVariant({
      variantId: completedVariantId,
      variantData
    }));

    setSelectedVariants((prev) => prev.filter((v) => v.id !== variantId));

    const newCompletedVariant = {
      id: completedVariantId,
      type: variantType,
      selectedOptions: selectedOptions || [],
    };

    setCompletedVariants((prev) => [...prev, newCompletedVariant]);
    if (originalCompletedVariantId) {
      setEditingVariantIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(originalCompletedVariantId);
        return newSet;
      });
      
      setEditingVariantOriginalData((prev) => {
        const newData = { ...prev };
        delete newData[originalCompletedVariantId];
        return newData;
      });
    }
  };

  const handleSaveVariantTypes = async () => {
    if (!isEditMode || !id) {
      ToastNotifyError("Cannot save variants in create mode");
      return;
    }

    if (!completedVariants || completedVariants?.length === 0) {
      ToastNotifyError("Please add at least one variant type");
      return;
    }

    try {
      const optionValuesDict = {};
      completedVariants?.forEach(v => {
        if (v?.type && v?.selectedOptions) {
          const typeId = String(v.type?.id);
          const valueIds = v?.selectedOptions?.map(opt => opt?.id)?.filter(Boolean);
          if (valueIds?.length > 0) {
            optionValuesDict[typeId] = valueIds;
          }
        }
      });

      const optionTypesArray = completedVariants?.map(cv => cv.type?.id)?.filter(Boolean);
      const uniqueOptionTypes = [...new Set(optionTypesArray)];

      const currentFormData = watch();

      const payload = {
        name: currentFormData.productName || "",
        description: currentFormData.description || "",
        option_types: uniqueOptionTypes,
        option_values_dict: optionValuesDict
      };

      const response = await dispatch(fetchUpdateProductOptions({
        productId: id,
        data: payload
      }));

      const { status, message: responseMessage } = response?.payload || {};
      if (status) {
        ToastNotifySuccess("Variant types saved successfully!");
        await refreshVariantsList();
        updateTotalQuantity();
      } else {
        ToastNotifyError(responseMessage || "Failed to save variant types");
      }
    } catch (error) {
      console.error("Error saving variant types:", error);
      rollbar.error("Error saving variant types", error);
      ToastNotifyError("Failed to save variant types");
    }
  };

  const handleRemoveCompletedVariant = (completedVariantId) => {
    const variantToRemove = completedVariants?.find((v) => v.id === completedVariantId);
    if (variantToRemove) {
      setVariantTypeToDelete(variantToRemove);
      setOpenDeleteVariantTypeModal(true);
    }
  };

  const handleConfirmDeleteVariantType = () => {
    if (!variantTypeToDelete) return;
    const completedVariantId = variantTypeToDelete?.id;
    const variantToRemove = completedVariants?.find((v) => v.id === completedVariantId);
    if (variantToRemove && variantToRemove.type) {
      if (!availableVariantTypes?.find((t) => t.id === variantToRemove.type?.id)) {
        setAvailableVariantTypes((prev) => [...prev, variantToRemove.type]);
      }
    }

    setCompletedVariants((prev) => prev.filter((v) => v.id !== completedVariantId));

    dispatch(actions.clearProductVariant({ variantId: completedVariantId }));

    if (editingVariantIds.has(completedVariantId)) {
      setEditingVariantIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(completedVariantId);
        return newSet;
      });
      
      setEditingVariantOriginalData((prev) => {
        const newData = { ...prev };
        delete newData[completedVariantId];
        return newData;
      });

      setSelectedVariants((prev) => prev.filter((v) => v.originalCompletedVariantId !== completedVariantId));
    }

    setOpenDeleteVariantTypeModal(false);
    setVariantTypeToDelete(null);
  };

  const handleDeleteVariantCombination = async (variantIndex) => {
    try {
      const combination = variantCombinations?.[variantIndex];
      const allVariantData = watch('variantCombinations') || [];
      const formData = allVariantData[variantIndex] || {};
      const variantId = formData.id || combination?.id;

      if (variantId && isEditMode) {
        const response = await dispatch(fetchDeleteVariant(variantId));
        const { status, message } = response?.payload || {};

        if (!status) {
          ToastNotifyError(message || "Failed to delete variant");
          return;
        }

        ToastNotifySuccess("Variant deleted successfully!");
      }

      const updatedCombinations = variantCombinations.filter((_, idx) => idx !== variantIndex);
      setVariantCombinations(updatedCombinations);

      const updatedFormData = allVariantData.filter((_, idx) => idx !== variantIndex);

      updatedFormData.forEach((variant, idx) => {
        setValue(`variantCombinations.${idx}.id`, variant.id);
        setValue(`variantCombinations.${idx}.price`, variant.price);
        setValue(`variantCombinations.${idx}.quantity`, variant.quantity);
        setValue(`variantCombinations.${idx}.media`, variant.media);
      });

      if (allVariantData?.length > updatedFormData?.length) {
        const lastIndex = allVariantData?.length - 1;
        setValue(`variantCombinations.${lastIndex}.id`, undefined);
        setValue(`variantCombinations.${lastIndex}.price`, undefined);
        setValue(`variantCombinations.${lastIndex}.quantity`, undefined);
        setValue(`variantCombinations.${lastIndex}.media`, undefined);
      }

      setVariantSelectedImages((prev) => {
        const newState = { ...prev };
        delete newState[variantIndex];
        const reindexed = {};
        Object.keys(newState).forEach((key) => {
          const oldIndex = parseInt(key);
          if (oldIndex > variantIndex) {
            reindexed[oldIndex - 1] = newState[key];
          } else if (oldIndex < variantIndex) {
            reindexed[oldIndex] = newState[key];
          }
        });
        return reindexed;
      });

      if (variantId && originalVariantQuantities[variantId]) {
        setOriginalVariantQuantities((prev) => {
          const newState = { ...prev };
          delete newState[variantId];
          return newState;
        });
      }
    } catch (error) {
      console.error("Error deleting variant:", error);
      rollbar.error("Error deleting variant", error);
      ToastNotifyError("Failed to delete variant");
    }
  };

  const handleEditCompletedVariant = (completedVariant) => {
    const storedVariantData = productVariants[completedVariant.id];

    setEditingVariantOriginalData((prev) => ({
      ...prev,
      [completedVariant.id]: {
        id: completedVariant.id,
        type: completedVariant.type,
        selectedOptions: completedVariant.selectedOptions || [],
      },
    }));

    setEditingVariantIds((prev) => new Set([...prev, completedVariant.id]));

    const variantToRemove = completedVariants?.find((v) => v.id === completedVariant.id);
    if (variantToRemove && variantToRemove.type) {
      if (!availableVariantTypes?.find((t) => t.id === variantToRemove.type?.id)) {
        setAvailableVariantTypes((prev) => [...prev, variantToRemove.type]);
      }
    }

    setCompletedVariants((prev) => prev.filter((v) => v.id !== completedVariant.id));

    const newVariant = {
      id: Date.now(),
      type: completedVariant.type,
      selectedOptions: storedVariantData?.selectedOptions || completedVariant.selectedOptions || [],
      variantOptions: storedVariantData?.variantOptions || variantOptionsMap[completedVariant.type?.id] || [],
      originalCompletedVariantId: completedVariant.id, // Store reference to original for discard
    };

    setSelectedVariants((prev) => [...prev, newVariant]);

    // Restore variant options in the map
    if (storedVariantData?.variantOptions && storedVariantData?.variantOptions?.length > 0) {
      setVariantOptionsMap((prev) => ({
        ...prev,
        [completedVariant.type?.id]: storedVariantData?.variantOptions,
      }));
    }

    setValue(`variants.${newVariant.id}.variantType`, completedVariant.type);
    if (newVariant?.selectedOptions && newVariant?.selectedOptions?.length > 0) {
      setValue(`variants.${newVariant.id}.variantOption`, newVariant.selectedOptions);
    }
    setTimeout(() => {
      setValue(`variants.${newVariant.id}.variantType`, completedVariant.type);
      if (newVariant?.selectedOptions && newVariant?.selectedOptions?.length > 0) {
        setValue(`variants.${newVariant.id}.variantOption`, newVariant.selectedOptions);
      }
    }, 200);
  };

  const handleDiscardCompletedVariant = (variantIdToDiscard = null) => {
    let editingVariantId = variantIdToDiscard;
    if (!editingVariantId) {
      const editingVariant = selectedVariants?.find((v) => 
        v?.originalCompletedVariantId && editingVariantIds.has(v?.originalCompletedVariantId)
      );
      editingVariantId = editingVariant?.originalCompletedVariantId;
    }

    if (!editingVariantId || !editingVariantOriginalData[editingVariantId]) return;

    const editingVariant = selectedVariants?.find((v) => v.originalCompletedVariantId === editingVariantId);
    if (!editingVariant) return;

    const originalData = editingVariantOriginalData[editingVariantId];
    const restoredCompletedVariant = {
      id: originalData?.id,
      type: originalData?.type,
      selectedOptions: originalData?.selectedOptions || [],
    };

    setCompletedVariants((prev) => [...prev, restoredCompletedVariant]);

    const variantId = editingVariant?.id;

    setSelectedVariants((prev) => prev?.filter((v) => v?.originalCompletedVariantId !== editingVariantId));

    if (editingVariant?.type) {
      setAvailableVariantTypes((prev) => prev.filter((t) => t.id !== editingVariant?.type?.id));
    }

    if (variantId) {
      setValue(`variants.${variantId}.variantType`, undefined);
      setValue(`variants.${variantId}.variantOption`, undefined);
    }

    setEditingVariantIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(editingVariantId);
      return newSet;
    });
    
    setEditingVariantOriginalData((prev) => {
      const newData = { ...prev };
      delete newData[editingVariantId];
      return newData;
    });
  };

  const handleOpenVariantImageModal = (variantIndex) => {
    setCurrentVariantIndex(variantIndex);

    // Restore previously selected images from form value
    const formValue = watch(`variantCombinations.${variantIndex}.media`);
    if (formValue && Array.isArray(formValue) && formValue?.length > 0) {
      // Find indices of selected images in productMedia
      const selectedIndices = formValue
        ?.map(url => productMedia?.findIndex(mediaUrl => mediaUrl === url))
        ?.filter(idx => idx !== -1);

      setVariantSelectedImages((prev) => ({
        ...prev,
        [variantIndex]: selectedIndices,
      }));
    } else {
      // Clear selection if no previous selection
      setVariantSelectedImages((prev) => ({
        ...prev,
        [variantIndex]: [],
      }));
    }

    setOpenVariantImageModal(true);
  };

  const handleCloseVariantImageModal = () => {
    // Clear selection state when canceling (don't save changes)
    if (currentVariantIndex !== null) {
      // Restore original selection if variant exists
      const combination = variantCombinations?.[currentVariantIndex];
      const variantId = combination?.id;

      if (variantId && originalVariantData[variantId]) {
        const originalData = originalVariantData[variantId];
        const originalMedia = Array.isArray(originalData?.media) ? originalData?.media : (originalData?.media ? [originalData?.media] : []);

        if (originalMedia?.length > 0 && originalMedia[0]) {
          // Find the index of the original image in productMedia
          const originalImageIndex = productMedia?.findIndex(url => url === originalMedia[0]);
          if (originalImageIndex !== -1) {
            setVariantSelectedImages((prev) => ({
              ...prev,
              [currentVariantIndex]: [originalImageIndex]
            }));
          } else {
            // Clear selection if original image not found
            setVariantSelectedImages((prev) => {
              const newState = { ...prev };
              delete newState[currentVariantIndex];
              return newState;
            });
          }
        } else {
          // No original image, clear selection
          setVariantSelectedImages((prev) => {
            const newState = { ...prev };
            delete newState[currentVariantIndex];
            return newState;
          });
        }
      } else {
        // No variant ID or original data, clear selection
        setVariantSelectedImages((prev) => {
          const newState = { ...prev };
          delete newState[currentVariantIndex];
          return newState;
        });
      }
    }

    setOpenVariantImageModal(false);
    setCurrentVariantIndex(null);
  };

  const handleToggleVariantImageSelection = (imageIndex) => {
    if (currentVariantIndex === null) return;

    // Single selection - set only this image index
    setVariantSelectedImages((prev) => ({
      ...prev,
      [currentVariantIndex]: [imageIndex]
    }));
  };

  const handleSaveVariantImages = () => {
    if (currentVariantIndex === null) return;

    const selectedIndices = variantSelectedImages?.[currentVariantIndex] || [];
    const selectedImageUrl = selectedIndices?.length > 0 ? productMedia?.[selectedIndices[0]] : null;

    const mediaArray = selectedImageUrl ? [selectedImageUrl] : [];
    setValue(`variantCombinations.${currentVariantIndex}.media`, mediaArray);

    handleVariantChange(currentVariantIndex, 'media', mediaArray);

    if (selectedImageUrl) {
      clearErrors(`variantCombinations.${currentVariantIndex}.media`);
    }

    setOpenVariantImageModal(false);
    setCurrentVariantIndex(null);
  };

  const calculateTotalQuantityFromVariants = () => {
    const allVariantData = watch('variantCombinations') || [];
    const totalQuantity = allVariantData.reduce((sum, variant) => {
      const quantity = Number(variant?.quantity) || 0;
      return sum + quantity;
    }, 0);
    return totalQuantity;
  };

  const updateTotalQuantity = () => {
    const allVariantData = watch('variantCombinations') || [];
    const hasVariants = variantCombinations?.length > 0 || allVariantData?.length > 0;

    if (hasVariants) {
      const totalQuantity = calculateTotalQuantityFromVariants();
      setValue('quantity', String(totalQuantity));
    }
  };

  const watchedVariantCombinations = watch('variantCombinations');
  useEffect(() => {
    const allVariantData = watchedVariantCombinations || [];
    const hasVariants = variantCombinations?.length > 0 || allVariantData?.length > 0;

    if (hasVariants) {
      updateTotalQuantity();
    }

    // Clear price and quantity validation errors if any variant has shopify_variant_id
    const hasShopifyVariant = variantCombinations?.some(variant => variant?.variantData?.shopify_variant_id);
    if (hasShopifyVariant) {
      clearErrors('price');
      clearErrors('quantity');
    }
  }, [watchedVariantCombinations, variantCombinations?.length, variantCombinations, clearErrors]);

  const refreshVariantsList = async () => {
    if (!isEditMode || !id) return;

    try {
      const res = await dispatch(fetchGetProductById(id));
      if (res?.payload?.status === 404) {
        return;
      }
      const { data, status } = res?.payload || {};

      if (status && data && data?.variants && Array.isArray(data?.variants) && data?.variants?.length > 0) {
        const mappedVariants = data?.variants?.map((variant) => {
          const optionValues = [];
          if (variant?.options && typeof variant?.options === 'object') {
            if (Array.isArray(variant?.options)) {
              optionValues.push(...variant?.options);
            } else {
              const firstKey = Object.keys(variant?.options)[0];
              if (!isNaN(firstKey)) {
                Object.entries(variant.options)?.forEach(([typeId, valueIds]) => {
                  if (Array.isArray(valueIds)) {
                    valueIds?.forEach(valueId => {
                      optionValues.push({ id: valueId, typeId: parseInt(typeId) });
                    });
                  }
                });
              }
            }
          }

          return {
            id: variant?.id,
            options: variant?.options,
            optionValues: optionValues,
            option_values_with_hex: variant?.option_values_with_hex,
            price: variant?.price || '',
            quantity: variant?.quantity || 0,
            media: variant?.variant_img || [],
            variant_title: variant?.variant_title,
            displayName: variant?.display_name || variant?.variant_title || `Variant ${variant?.id}`,
            display_name: variant?.display_name,
            variantData: variant
          };
        });

        setVariantCombinations(mappedVariants);

        const originalQuantities = {};
        const originalData = {};
        mappedVariants?.forEach((variant, index) => {
          setValue(`variantCombinations.${index}.id`, variant?.id);
          setValue(`variantCombinations.${index}.price`, variant?.price);
          setValue(`variantCombinations.${index}.quantity`, variant?.quantity);
          setValue(`variantCombinations.${index}.media`, variant?.media);

          if (variant?.id) {
            originalQuantities[variant?.id] = variant?.quantity || 0;
            originalData[variant?.id] = {
              price: variant?.price || '',
              quantity: variant?.quantity || 0,
              media: variant?.media || []
            };
          }
        });
        setOriginalVariantQuantities(originalQuantities);
        setOriginalVariantData(originalData);

        updateTotalQuantity();
      }
    } catch (error) {
      console.error("Error refreshing variants:", error);
      rollbar.error("Error refreshing variants", error);
    }
  };

  const handleSaveVariant = async (variantIndex) => {
    try {
      const allVariantData = watch('variantCombinations') || [];
      const formData = allVariantData[variantIndex] || {};
      const combination = variantCombinations?.[variantIndex];
      const variantId = formData.id || combination?.id;

      if (!variantId) {
        ToastNotifyError("Variant ID not found");
        return;
      }

      const newQuantity = Number(formData.quantity) || 0;
      const variantImages = formData.media && Array.isArray(formData.media)
        ? formData.media
        : (formData.media ? [formData.media] : []);

      // Validate: if quantity was updated from 0 to > 0, image is required
      const originalData = originalVariantData?.[variantId];
      if (originalData) {
        const originalQuantity = originalData?.quantity || 0;

        // If quantity was updated (changed from original value), image is required
        if (originalQuantity !== newQuantity) {
          // Quantity was updated - image is required
          if (!variantImages || variantImages?.length === 0) {
            setError(`variantCombinations.${variantIndex}.media`, {
              type: 'manual',
              message: 'Image is required when quantity is updated'
            });
            ToastNotifyError("Image is required when quantity is updated");
            return;
          }
        }
      }

      // Prepare payload for variant update
      const payload = {
        price: formData.price ? String(formData.price) : null,
        quantity: newQuantity,
        variant_img: variantImages,
        // Include SKU if available
        ...(formData.sku && { sku: formData.sku }),
      };

      // Call API to update variant
      const response = await dispatch(fetchUpdateVariant({
        variantId: variantId,
        data: payload
      }));

      // Check if response has error (axios error structure)
      // When axios throws an error, the thunk returns the error object
      // The error response data is in response.payload.response.data
      if (response?.error || response?.payload?.response) {
        // Extract error message from axios error response
        const errorData = response?.payload?.response?.data || response?.payload;
        const errorMessage = errorData?.message || "Failed to save variant";
        ToastNotifyError(errorMessage);
        return;
      }

      const { status, message } = response?.payload || {};

      // Check if status is explicitly false (API returned error response)
      if (status === false) {
        const errorMessage = message || response?.payload?.message || "Failed to save variant";
        ToastNotifyError(errorMessage);
        return;
      }

      if (status) {
        ToastNotifySuccess("Variant saved successfully!");

        await refreshVariantsList();

        updateTotalQuantity();

        setModifiedVariants(prev => {
          const newSet = new Set(prev);
          newSet.delete(variantId);
          return newSet;
        });

        // Clear any errors
        clearErrors(`variantCombinations.${variantIndex}.media`);
      } else {
        // Fallback error handling
        const errorMessage = message || response?.payload?.message || "Failed to save variant";
        ToastNotifyError(errorMessage);
      }
    } catch (error) {
      console.error("Error saving variant:", error);
      rollbar.error("Error saving variant", error);
      // Try to extract error message from catch block
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save variant";
      ToastNotifyError(errorMessage);
    }
  };

  // Handle discarding variant changes
  const handleDiscardVariant = (variantIndex) => {
    try {
      const combination = variantCombinations?.[variantIndex];
      const variantId = combination?.id;

      if (!variantId || !originalVariantData?.[variantId]) {
        ToastNotifyError("Cannot discard changes - original data not found");
        return;
      }

      const originalData = originalVariantData?.[variantId];

      // Revert form values to original
      setValue(`variantCombinations.${variantIndex}.price`, originalData?.price);
      setValue(`variantCombinations.${variantIndex}.quantity`, originalData?.quantity);
      setValue(`variantCombinations.${variantIndex}.media`, originalData?.media);

      // Clear or update variantSelectedImages to match original media
      const originalMedia = Array.isArray(originalData?.media) ? originalData?.media : (originalData?.media ? [originalData?.media] : []);
      if (originalMedia?.length > 0 && originalMedia[0]) {
        // Find the index of the original image in productMedia
        const originalImageIndex = productMedia?.findIndex(url => url === originalMedia[0]);
        if (originalImageIndex !== -1) {
          // Set the selection to the original image index
          setVariantSelectedImages((prev) => ({
            ...prev,
            [variantIndex]: [originalImageIndex]
          }));
        } else {
          // Original image not found in productMedia, clear selection
          setVariantSelectedImages((prev) => {
            const newState = { ...prev };
            delete newState[variantIndex];
            return newState;
          });
        }
      } else {
        // No original image, clear selection
        setVariantSelectedImages((prev) => {
          const newState = { ...prev };
          delete newState[variantIndex];
          return newState;
        });
      }

      // Remove from modified variants
      setModifiedVariants(prev => {
        const newSet = new Set(prev);
        newSet.delete(variantId);
        return newSet;
      });

      // Clear any errors
      clearErrors(`variantCombinations.${variantIndex}.media`);

      ToastNotifySuccess("Changes discarded");
    } catch (error) {
      console.error("Error discarding variant:", error);
      rollbar.error("Error discarding variant", error);
      ToastNotifyError("Failed to discard changes");
    }
  };

  // Track variant changes
  const handleVariantChange = (variantIndex, field, value) => {
    const combination = variantCombinations?.[variantIndex];
    const variantId = combination?.id;

    if (!variantId || !originalVariantData[variantId]) return;

    const originalData = originalVariantData[variantId];
    let isModified = false;

    if (field === 'price') {
      isModified = String(originalData.price || '') !== String(value || '');
    } else if (field === 'quantity') {
      isModified = Number(originalData.quantity || 0) !== Number(value || 0);
      updateTotalQuantity();
    } else if (field === 'media') {
      const originalMedia = Array.isArray(originalData.media) ? originalData.media : (originalData.media ? [originalData.media] : []);
      const newMedia = Array.isArray(value) ? value : (value ? [value] : []);
      isModified = JSON.stringify(originalMedia.sort()) !== JSON.stringify(newMedia.sort());
    }

    setModifiedVariants(prev => {
      const newSet = new Set(prev);
      if (isModified) {
        newSet.add(variantId);
      } else {
        newSet.delete(variantId);
      }
      return newSet;
    });
  };

  return (
    <Box>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit, onError)}
        id='add-edit-product-form'
        sx={styles.formContainer}
        noValidate
      >
        <Box sx={styles.backBtnContainer}>
          <ArrowBackIcon sx={styles.backBtn} onClick={handleBack} />
          <Typography sx={styles.title}>
            {isEditMode ? "Edit product" : "Add new product"}
          </Typography>
        </Box>
        <Box sx={styles.productInformationSection}>
          <Typography sx={styles.subHeading}>
            Product Information
          </Typography>
          <Grid container spacing={{ xs: 1, md: 2 }}>
            <Grid item xs={12}>
              <Controller
                name='productName'
                control={control}
                defaultValue=''
                render={({ field }) => (
                  <TextFeild
                    {...field}
                    id='productName'
                    required
                    label={INPUT_LABELS.productName}
                    placeholder='Product name'
                    variant='outlined'
                    fullWidth
                    margin='normal'
                    error={!!errors.productName}
                    helperText={errors.productName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name='Categories'
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <MUIMultipleSelect
                    name='Categories'
                    id='Categories'
                    required
                    label={INPUT_LABELS.Categories}
                    placeholder='Categories'
                    error={!!errors.Categories}
                    helperText={errors.Categories?.message}
                    options={categories}
                    objectOptions={true}
                    onChange={(selectedOptions) =>
                      field.onChange(selectedOptions.target.value)
                    }
                    value={field.value}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name='status'
                control={control}
                defaultValue=''
                render={({ field }) => (
                  <SelectFeild
                    {...field}
                    id='status'
                    required
                    label={INPUT_LABELS.status}
                    placeholder='Select Status'
                    options={statusOptions}
                    error={!!errors.status}
                    helperText={errors.status?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name='tags'
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <MUIMultipleSelect
                    name='tags'
                    id='tags'
                    required
                    label={INPUT_LABELS.tags}
                    placeholder='Select Tags'
                    error={!!errors.tags}
                    helperText={errors.tags?.message}
                    options={tags}
                    objectOptions={true}
                    onChange={(selectedOptions) =>
                      field.onChange(selectedOptions.target.value)
                    }
                    value={field.value}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Box>
            <Controller
              name='description'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <RichTextEditor
                  {...field}
                  id='description'
                  label={INPUT_LABELS.description}
                  placeholder='Enter description here'
                  fullWidth
                  margin='normal'
                  maxHeight='200px'
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Box>

          {/* Media Section - Product level, just below Description */}
          <Box>
            <Controller
              name="productMedia"
              control={control}
              defaultValue={[]}
              render={({ field }) => {
                // Sync field value with productMedia state
                const currentMedia = field.value && field.value?.length > 0 ? field.value : productMedia;

                return (
                  <>
                    <MediaSection
                      productMedia={currentMedia}
                      setProductMedia={(newMedia) => {
                        setProductMedia(newMedia);
                        field.onChange(newMedia);
                        // Trigger validation
                        setValue('productMedia', newMedia, { shouldValidate: true });
                      }}
                      setValue={setValue}
                      handleRemoveMedia={(mediaIndex) => {
                        const updatedMedia = currentMedia?.filter((_, idx) => idx !== mediaIndex);
                        setProductMedia(updatedMedia);
                        field.onChange(updatedMedia);
                        // Trigger validation
                        setValue('productMedia', updatedMedia, { shouldValidate: true });
                      }}
                    />
                    {errors.productMedia && (
                      <Typography
                        sx={{
                          color: theme.palette?.error?.main || "#d32f2f",
                          fontSize: "12px",
                          marginTop: "4px",
                          marginLeft: "14px",
                        }}
                      >
                        {errors.productMedia?.message}
                      </Typography>
                    )}
                  </>
                );
              }}
            />
          </Box>
        </Box>
        {(() => {
          const hasShopifyVariant = variantCombinations?.some(variant => variant?.variantData?.shopify_variant_id);
          if (hasShopifyVariant) {
            return null;
          }
          
          return (
            <Box sx={styles.pricingQuantitySection}>
              <Typography sx={styles.subHeading}>
                Pricing & Quantity
              </Typography>
              <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name='price'
                    control={control}
                    defaultValue=''
                    render={({ field }) => (
                      <TextFeild
                        {...field}
                        id='price'
                        required={!hasShopifyVariant}
                        type='number'
                        label={INPUT_LABELS.priceinUSD}
                        placeholder='Enter Price (in USD)'
                        variant='outlined'
                        fullWidth
                        margin='normal'
                        error={!!errors.price}
                        helperText={errors.price?.message}
                        inputProps={{
                          min: 0,
                          step: "0.01",
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name='quantity'
                    control={control}
                    defaultValue=''
                    render={({ field }) => {
                      const allVariantData = watch('variantCombinations') || [];
                      const hasVariants = variantCombinations?.length > 0 || allVariantData?.length > 0;
                      const hasShopifyVariant = variantCombinations?.some(variant => variant?.variantData?.shopify_variant_id);

                      const totalQuantity = calculateTotalQuantityFromVariants();

                      const isDisabled = hasVariants;

                      const displayValue = hasVariants ? String(totalQuantity) : field.value;

                      return (
                        <TextFeild
                          {...field}
                          id='quantity'
                          required={!hasVariants && !hasShopifyVariant}
                          disabled={isDisabled}
                          type='number'
                          label={INPUT_LABELS.totalQuantity}
                          placeholder='Enter total quantity'
                          variant='outlined'
                          fullWidth
                          margin='normal'
                          error={!!errors.quantity}
                          helperText={errors.quantity?.message || (isDisabled ? 'Total quantity is calculated from variants' : '')}
                          inputProps={{
                            min: 0,
                            step: 1,
                          }}
                          value={displayValue}
                          onChange={(e) => {
                            if (!hasVariants) {
                              const newValue = e.target.value;
                              const currentValue = field?.value?.toString() || '';

                              if (currentValue === '0' && newValue?.length > 1 && newValue?.startsWith('0')) {
                                const replacedValue = newValue?.replace(/^0+/, '') || '0';
                                field.onChange({ ...e, target: { ...e.target, value: replacedValue } });
                              } else {
                                field.onChange(e);
                              }
                            }
                          }}
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })()}
      </Box>
      {(hasVariants || isEditMode) && (
        <VariantSection
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
          selectedVariants={selectedVariants}
          completedVariants={completedVariants}
          variantCombinations={variantCombinations}
          variantOptionsMap={variantOptionsMap}
          availableVariantTypes={availableVariantTypes}
          productMedia={productMedia}
          variantSelectedImages={variantSelectedImages}
          openVariantImageModal={openVariantImageModal}
          currentVariantIndex={currentVariantIndex}
          isEditMode={isEditMode}
          setSelectedVariants={setSelectedVariants}
          setVariantOptionsMap={setVariantOptionsMap}
          setAvailableVariantTypes={setAvailableVariantTypes}
          handleRemoveVariant={handleRemoveVariant}
          handleVariantDone={handleVariantDone}
          handleRemoveCompletedVariant={handleRemoveCompletedVariant}
          handleEditCompletedVariant={handleEditCompletedVariant}
          handleDiscardCompletedVariant={handleDiscardCompletedVariant}
          editingVariantIds={editingVariantIds}
          handleOpenVariantImageModal={handleOpenVariantImageModal}
          handleCloseVariantImageModal={handleCloseVariantImageModal}
          handleToggleVariantImageSelection={handleToggleVariantImageSelection}
          handleSaveVariantImages={handleSaveVariantImages}
          setProductMedia={setProductMedia}
          setVariantSelectedImages={setVariantSelectedImages}
          handleDeleteVariantCombination={handleDeleteVariantCombination}
          optionTypes={optionTypes}
          handleSaveVariant={handleSaveVariant}
          handleDiscardVariant={handleDiscardVariant}
          handleVariantChange={handleVariantChange}
          handleSaveVariantTypes={handleSaveVariantTypes}
          modifiedVariants={modifiedVariants}
          originalVariantData={originalVariantData}
        />
      )}

      <Box
        sx={{
          width: { xs: "89%", sm: "85%", md: "80%" },
          pl: { xs: "112px" },
          margin: "0 auto",
          mt: 4,
          mb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Button
          type="button"
          onClick={handleSaveButtonClick}
          variant="contained"
          disabled={selectedVariants?.length > 0 || editingVariantIds.size > 0}
          sx={{
            backgroundColor: theme.palette?.primary?.main || '#0F0F0F',
            color: '#FFFFFF',
            fontFamily: 'Lexend',
            fontSize: '16px',
            fontWeight: 400,
            textTransform: 'none',
            px: 4,
            py: 1.5,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette?.primary?.dark || '#000000',
            },
            '&:disabled': {
              backgroundColor: theme.palette?.action?.disabledBackground || '#E0E0E0',
              color: theme.palette?.action?.disabled || '#9E9E9E',
            },
          }}
        >
          {isEditMode ? "Save & Continue" : "Save product"}
        </Button>
      </Box>

      {/* Product Variants Modal */}
      <ProductVariantsModal
        open={openVariantModal}
        onClose={() => setOpenVariantModal(false)}
        onSaveAsSingleProduct={handleSaveAsSingleProduct}
        onAddVariants={handleAddVariants}
      />

      <VariantTypeDeleteModal
        open={openDeleteVariantTypeModal}
        onClose={() => {
          setOpenDeleteVariantTypeModal(false);
          setVariantTypeToDelete(null);
        }}
        onConfirm={handleConfirmDeleteVariantType}
        variantTypeName={variantTypeToDelete?.type?.name}
      />
    </Box>
  );
};

export default AddEditProduct;